import { Injectable } from '@angular/core';
import { logFeature } from '@office-start/telemetry-service';
import { KnownFeatureNames, KnownRequestInstrumentationEvents } from '@office-start/telemetry-types';

import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { InstrumentationParam, InstrumentationService, KnownInstrumentationEvents } from '../../shared/instrumentation/instrumentation.service';
import { AccessTokenConsumer, AccessTokenService, CAPDialogData } from '../../shared/services/access-token.service';
import { OfficeHomeApiConfigService } from '../../shared/services/officehome-api-config.service';
import { WindowService } from '../../shared/services/window.service';
import { AuthType, ClientStartupConfigService } from '../../shared/shared-context/client-startup-config.service';
import { ContextService } from '../../shared/shared-context/context.service';
import { PwaConfigService } from './pwa-config.service';

@Injectable()
export class PwaAccessTokenService extends AccessTokenService {
    private readonly wamAccessTokenRequests: Map<string, AccessTokenRequest>;
    private readonly resourceMap: Map<string, string>;

    constructor(
        private readonly clientStartupConfigService: ClientStartupConfigService,
        private readonly contextService: ContextService,
        errorHandler: OfficeHomePageErrorHandler,
        private readonly pwaConfigService: PwaConfigService,
        private readonly windowService: WindowService,
        private readonly instrumentationService: InstrumentationService,
        private readonly officeApiService: OfficeHomeApiConfigService
    ) {
        super();
        this.init(errorHandler, clientStartupConfigService.config.authType === AuthType.MSA ? AccessTokenFormat.RPSCompact : AccessTokenFormat.JWT, AccessTokenSource.WAM);
        this.wamAccessTokenRequests = this.windowService.nativeWindow.wamAccessTokenRequests || new Map<string, AccessTokenRequest>();
        this.resourceMap = this.pwaConfigService.config.resourceUrlMap ? new Map(Object.entries(this.pwaConfigService.config.resourceUrlMap)) : new Map();

        // Override the shellTokenRequestDictionary functions initialized in office-pwa.ts because this class checks for token validity and
        // caches them (in wamAccessTokenRequests promises)
        const shellTokenRequestDictionary = this.windowService.nativeWindow.shellTokenRequestDictionary;
        if (shellTokenRequestDictionary && this.resourceMap.size > 0) {
            [AccessTokenConsumer.Graph, AccessTokenConsumer.WCSS].forEach(resource => {
                shellTokenRequestDictionary[this.resourceMap.get(resource)] = () => this.getAccessTokenResponse(resource).then(response => response.token);
            });
        }
    }

    public hasPermanentAuthError(): Promise<boolean> {
        throw new Error('Method not implemented.');
    }

    public hasOneDriveToken(): boolean {
        // NOOP: Assume Tokens are fetched correctly by WAM
        // TODO: Figure out how to handle slow tokens/tokens that need to be refreshed
        return true;
    }

    public hasCAPError(_tokenConsumer: AccessTokenConsumer, _resourceUrl?: string): boolean {
        // Not sure if it's possible to check for CAP error from WAM.
        return false;
    }

    // TODO: Observe forceFetch param
    protected getAccessTokenResponse(
        tokenConsumer: AccessTokenConsumer,
        forceFetch?: boolean,
        resourceUrl?: string,
        _allowInteractive?: boolean,
        _capDialogData?: CAPDialogData,
        clientId?: string,
        _scope?: string,
        _claims?: Array<string>,
        claimsChallenge?: string): Promise<AccessTokenResponse> {
        return this.getAccessTokenPromise(tokenConsumer, resourceUrl, forceFetch, clientId, claimsChallenge);
    }

    private getAccessTokenPromise(tokenConsumer: AccessTokenConsumer, resourceUrl: string, forceFetch?: boolean, clientId?: string, claimsChallenge?: string): Promise<AccessTokenResponse> {
        const startupConfig = this.clientStartupConfigService.config;
        let tokenFormat: AccessTokenFormat;
        if (tokenConsumer === AccessTokenConsumer.OfficeHome && startupConfig.sharedClientStartupContext.pwaV2TokenEnabled && this.officeApiService.config.jwtAuthEnabled) {
            // V2 OfficeHome token is JWT for MSA and AAD
            tokenFormat = AccessTokenFormat.JWT;
        } else {
            tokenFormat = startupConfig.authType === AuthType.MSA ? AccessTokenFormat.RPSCompact : AccessTokenFormat.JWT;
        }

        if (this.contextService.sharedContext.inMockMode) {
            return Promise.resolve({ success: true, token: 'mock token', format: tokenFormat, source: AccessTokenSource.WAM });
        }

        let resource;
        let resultPromise: Promise<AccessTokenResponse>;
        const resourceFriendlyName = tokenConsumer;

        if (!resourceUrl) {
            resource = this.resourceMap.get(resourceFriendlyName);
        } else {
            resource = resourceUrl;
        }

        if (!resource) {
            return Promise.reject('Invalid resource');
        }

        if (this.wamAccessTokenRequests.has(resource)) {
            const accessTokenPromise: Promise<AccessTokenResponse> = this.wamAccessTokenRequests.get(resource).result;
            resultPromise = accessTokenPromise.then(tokenResponse => {
                if (this.wamAccessTokenRequests.get(resource).pending) {
                    this.wamAccessTokenRequests.get(resource).pending = false;
                    this.logAccessTokenState('Succeeded', resourceFriendlyName);
                    return tokenResponse;
                }

                if (!this.tokenIsValid(tokenResponse) || forceFetch) {
                    const refreshAccessToken = this.windowService.nativeWindow.getWamAccessToken(resource, resourceFriendlyName, clientId, claimsChallenge, forceFetch);
                    this.logAccessTokenState('RefreshTokenRequested', resourceFriendlyName);
                    this.wamAccessTokenRequests.set(resource, { pending: true, result: refreshAccessToken });
                    return refreshAccessToken;
                }

                this.logAccessTokenState('Fetched', resourceFriendlyName);
                return tokenResponse;
            });
        } else {
            resultPromise = this.windowService.nativeWindow.getWamAccessToken(resource, resourceFriendlyName, clientId, claimsChallenge, forceFetch);
            this.wamAccessTokenRequests.set(resource, { pending: true, result: resultPromise });
        }

        return resultPromise.catch(() => {
            // If token fetch failed we should retry token fetch by removing resource from wamAccessTokenRequests
            // so subsequent token fetch can retry calling getWamAccessToken
            if (this.wamAccessTokenRequests.has(resource)) {
                this.wamAccessTokenRequests.delete(resource);
            }
            // getWamAccessToken should have telemetry already so we don't include more information in the error here
            return { success: false, token: '', format: tokenFormat, source: AccessTokenSource.WAM, error: new Error('Token fetch failed from WAM') };
        });
    }

    private tokenIsValid(tokenResponse: AccessTokenResponse): boolean {
        return tokenResponse && (tokenResponse.format !== AccessTokenFormat.JWT || !tokenResponse.expiryMs || tokenResponse.expiryMs > new Date().getTime());
    }

    private logAccessTokenState(state: string, resourceFriendlyName: string) {
        const params = {
            [InstrumentationParam.ResourceUrl]: resourceFriendlyName,
            [InstrumentationParam.State]: state
        };

        this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.WamTokenRequest, params);
        logFeature({ featureName: KnownFeatureNames.Hwa, eventName: KnownRequestInstrumentationEvents.WamTokenRequest, params });
    }
 }
